<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content package-detail-modal" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h3>
            <i class="fas fa-box-open"></i>
            Détails du Package
          </h3>
          <p class="modal-subtitle">{{ package.nom }}</p>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Informations générales -->
        <div class="section">
          <div class="section-header">
            <i class="fas fa-info-circle"></i>
            <h4>Informations Générales</h4>
          </div>
          <div class="section-content">
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-tag"></i>
                  Nom
                </span>
                <span class="info-value">{{ package.nom }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-barcode"></i>
                  Référence
                </span>
                <span class="info-value">{{ package.reference }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-toggle-on"></i>
                  Statut
                </span>
                <span class="info-value">
                  <span 
                    class="status-badge" 
                    :class="package.actif ? 'badge-active' : 'badge-inactive'"
                  >
                    {{ package.actif ? 'Actif' : 'Inactif' }}
                  </span>
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-boxes"></i>
                  Stock
                </span>
                <span class="info-value">
                  <span class="stock-badge" :class="getStockClass(package.quantiteStock)">
                    {{ package.quantiteStock }} unités
                  </span>
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-euro-sign"></i>
                  Prix Unitaire
                </span>
                <span class="info-value price-value">{{ formatPrice(package.prixUnitaire) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">
                  <i class="fas fa-calendar-plus"></i>
                  Date de Création
                </span>
                <span class="info-value">{{ formatDate(package.dateCreation) }}</span>
              </div>
            </div>

            <div class="description-box" v-if="package.description">
              <div class="description-header">
                <i class="fas fa-align-left"></i>
                <strong>Description</strong>
              </div>
              <p>{{ package.description }}</p>
            </div>
          </div>
        </div>

        <!-- Produits inclus -->
        <div class="section">
          <div class="section-header">
            <i class="fas fa-cubes"></i>
            <h4>Produits Inclus ({{ package.produits?.length || 0 }})</h4>
          </div>
          <div class="section-content">
            <div v-if="package.produits && package.produits.length > 0" class="products-table-container">
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Référence</th>
                    <th>Catégorie</th>
                    <th>Quantité</th>
                    <th>Prix Unitaire</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in package.produits" :key="getProductId(item)">
                    <td>
                      <div class="product-cell">
                        <i class="fas fa-cube"></i>
                        <span>{{ getProductName(item) }}</span>
                      </div>
                    </td>
                    <td>{{ getProductReference(item) }}</td>
                    <td>
                      <span class="category-badge" v-if="getProductCategory(item)">{{ getProductCategory(item) }}</span>
                      <span v-else class="no-data">-</span>
                    </td>
                    <td>
                      <span class="quantity-badge">x{{ item.quantite }}</span>
                    </td>
                    <td>{{ formatPrice(getProductPrice(item)) }}</td>
                    <td class="total-cell">
                      {{ formatPrice(getProductPrice(item) * item.quantite) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="total-row">
                    <td colspan="5" class="total-label">
                      <strong>Coût Total des Produits</strong>
                    </td>
                    <td class="total-value">
                      <strong>{{ formatPrice(calculateTotalCost()) }}</strong>
                    </td>
                  </tr>
                  <tr class="margin-row">
                    <td colspan="5" class="total-label">
                      <strong>Marge</strong>
                    </td>
                    <td class="margin-value" :class="getMarginClass()">
                      <strong>{{ formatPrice(calculateMargin()) }}</strong>
                      <span class="margin-percentage">({{ calculateMarginPercentage() }}%)</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div v-else class="no-products">
              <i class="fas fa-inbox"></i>
              <p>Aucun produit dans ce package</p>
            </div>
          </div>
        </div>

        <!-- Statistiques -->
        <div class="section">
          <div class="section-header">
            <i class="fas fa-chart-bar"></i>
            <h4>Statistiques</h4>
          </div>
          <div class="section-content">
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-icon" style="background: linear-gradient(135deg, #3b82f6, #2563eb);">
                  <i class="fas fa-cubes"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-value">{{ package.produits?.length || 0 }}</div>
                  <div class="stat-label">Produits Différents</div>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon" style="background: linear-gradient(135deg, #10b981, #059669);">
                  <i class="fas fa-boxes"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-value">{{ calculateTotalQuantity() }}</div>
                  <div class="stat-label">Quantité Totale</div>
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-icon" style="background: linear-gradient(135deg, #f59e0b, #d97706);">
                  <i class="fas fa-euro-sign"></i>
                </div>
                <div class="stat-content">
                  <div class="stat-value">{{ formatPrice(package.quantiteStock * package.prixUnitaire) }}</div>
                  <div class="stat-label">Valeur en Stock</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-close">
          <i class="fas fa-times"></i>
          Fermer
        </button>
        <button @click="$emit('edit', package)" class="btn-edit">
          <i class="fas fa-edit"></i>
          Modifier
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'PackageDetailModal',
  props: {
    package: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'edit'],
  setup(props, { emit }) {
    // Debug: Afficher les données du package
    console.log('Package reçu dans DetailModal:', props.package)
    console.log('Produits du package:', props.package.produits)
    
    const closeModal = () => {
      emit('close')
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getStockClass = (stock) => {
      if (stock === 0) return 'stock-zero'
      if (stock < 10) return 'stock-low'
      return 'stock-ok'
    }

    const getProductId = (item) => {
      if (typeof item.produit === 'object' && item.produit._id) {
        return item.produit._id
      }
      return item.produit || item._id
    }

    const getProductName = (item) => {
      if (typeof item.produit === 'object') {
        return item.produit.designation || item.produit.nom || 'Produit inconnu'
      }
      return 'Produit non chargé'
    }

    const getProductReference = (item) => {
      if (typeof item.produit === 'object') {
        return item.produit.referenceInterne || item.produit.reference || '-'
      }
      return '-'
    }

    const getProductCategory = (item) => {
      if (typeof item.produit === 'object') {
        return item.produit.categorie || null
      }
      return null
    }

    const getProductPrice = (item) => {
      if (typeof item.produit === 'object') {
        return item.produit.prixVente || item.produit.prixUnitaire || item.produit.prixAchat || 0
      }
      return 0
    }

    const calculateTotalCost = () => {
      if (!props.package.produits || props.package.produits.length === 0) return 0
      return props.package.produits.reduce((total, item) => {
        const prix = getProductPrice(item)
        return total + (prix * item.quantite)
      }, 0)
    }

    const calculateMargin = () => {
      return props.package.prixUnitaire - calculateTotalCost()
    }

    const calculateMarginPercentage = () => {
      if (props.package.prixUnitaire === 0) return '0.00'
      const margin = calculateMargin()
      return ((margin / props.package.prixUnitaire) * 100).toFixed(2)
    }

    const getMarginClass = () => {
      const margin = calculateMargin()
      if (margin < 0) return 'margin-negative'
      if (margin === 0) return 'margin-zero'
      return 'margin-positive'
    }

    const calculateTotalQuantity = () => {
      if (!props.package.produits || props.package.produits.length === 0) return 0
      return props.package.produits.reduce((total, item) => total + item.quantite, 0)
    }

    return {
      closeModal,
      formatPrice,
      formatDate,
      getStockClass,
      getProductId,
      getProductName,
      getProductReference,
      getProductCategory,
      getProductPrice,
      calculateTotalCost,
      calculateMargin,
      calculateMarginPercentage,
      getMarginClass,
      calculateTotalQuantity
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.modal-header {
  padding: 2rem;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
  font-weight: 500;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
}

.section-header i {
  color: #8b5cf6;
  font-size: 1.25rem;
}

.section-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.section-content {
  padding-left: 2.25rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-label i {
  color: #8b5cf6;
  font-size: 0.875rem;
}

.info-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.price-value {
  color: #8b5cf6;
  font-size: 1.5rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.875rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-active {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.badge-inactive {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.stock-badge {
  padding: 0.375rem 0.875rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
}

.stock-ok {
  background: #d1fae5;
  color: #065f46;
}

.stock-low {
  background: #fed7aa;
  color: #92400e;
}

.stock-zero {
  background: #fee2e2;
  color: #991b1b;
}

.description-box {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f9fafb, #f3f4f6);
  border-radius: 12px;
  border-left: 4px solid #8b5cf6;
}

.description-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #8b5cf6;
}

.description-box p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
}

/* Table des produits */
.products-table-container {
  overflow-x: auto;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.products-table thead {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.products-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.products-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.products-table tbody tr:hover {
  background: #f9fafb;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.product-cell i {
  color: #8b5cf6;
  font-size: 1.125rem;
}

.category-badge {
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #ddd6fe, #c4b5fd);
  color: #5b21b6;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.quantity-badge {
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
}

.total-cell {
  font-weight: 600;
  color: #8b5cf6;
}

.products-table tfoot {
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
}

.total-row td,
.margin-row td {
  padding: 1rem;
  font-size: 1.125rem;
}

.total-label {
  text-align: right;
  color: #6b7280;
}

.total-value {
  color: #1f2937;
  font-size: 1.25rem;
}

.margin-value {
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.margin-positive {
  color: #059669;
}

.margin-zero {
  color: #6b7280;
}

.margin-negative {
  color: #dc2626;
}

.margin-percentage {
  font-size: 0.875rem;
  opacity: 0.8;
}

.no-products {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.no-products i {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.no-products p {
  margin: 0;
  font-size: 1.125rem;
}

/* Statistiques */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: #8b5cf6;
}

.stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  color: white;
  font-size: 1.5rem;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-close,
.btn-edit {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-close {
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
}

.btn-close:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-edit {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.btn-edit:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column;
  }

  .btn-close,
  .btn-edit {
    width: 100%;
    justify-content: center;
  }
}
</style>
